import {
  SignUpFormComponentProps,
  SignUpFormFields,
} from 'app/components/SignUp/SignUpForm/types';
import { useAppDispatch } from 'app/store';
import { useNavigate } from 'react-router-dom';
import {
  useCountryQuery,
  useQueryParams,
  useReferralParam,
  useSignUpFormSteps,
  useSignUpFormValidations,
} from 'app/components/SignUp/SignUpForm/hooks';
import { useRef, useState } from 'react';
import {
  ApiErrorCodeMessages,
  ApiErrorCodeTypes,
  SignUpFormStep,
} from 'types/enums';
import { FormProvider, useForm } from 'react-hook-form';
import { signUpFormDefaultValues } from 'app/components/SignUp/SignUpForm/constants';
import {
  formatApiRequestData,
  getIsBasicFormValid,
  getIsDetailsFormValid,
  getUserDataFromQuery,
} from 'app/components/SignUp/SignUpForm/utils';
import { apiErrorHandler, dynamicYupResolver } from 'app/utils/utils';
import axios from 'axios';
import { SignUpResponse } from 'types/auth';
import {
  signUpAccountCreatedAction,
  signUpMarketingOptInAction,
} from 'app/store/Mixpanel/slice';
import { routes } from 'app/routing/routesConfig';
import { addAlertAction } from 'app/store/Alerts/slice';
import { chakra } from '@chakra-ui/react';
import { SignUpBasicForm } from 'app/components/SignUp/SignUpBasicForm';
import { SignUpDetailsForm } from 'app/components/SignUp/SignUpDetailsForm';
import { SignUpPersonaForm } from 'app/components/SignUp/SignUpPersonaForm';
import { SignUpActions } from 'app/components/SignUp/SignUpActions';

export const SignUpFormComponent: React.FC<SignUpFormComponentProps> = ({
  signUpLink,
  search,
  pathname,
  userPersonas,
  inviteToken,
  passwordPolicy,
  countryOptions,
  afterSubmit,
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const query = useQueryParams();
  const isUBC = query.get('referralName') === 'ubc';
  const emailParamExists = Boolean(query.get('email'));

  const [formStep, setFormStep] = useState<SignUpFormStep>(
    SignUpFormStep.BASIC
  );
  const referral = useRef<string>();

  const {
    concatenatedSchema,
    referralValidationSchema,
  } = useSignUpFormValidations({
    signUpLink,
    passwordPolicy,
  });

  const form = useForm<SignUpFormFields>({
    defaultValues: {
      ...signUpFormDefaultValues,
      ...getUserDataFromQuery(query, countryOptions),
    },
    mode: 'all',
    context: {
      yupValidationSchema: concatenatedSchema,
    },
    resolver: dynamicYupResolver,
  });

  const {
    handleSubmit,
    formState: { isValid, isDirty, isSubmitting, errors },
    setError,
    setValue,
    watch,
  } = form;

  const values = watch();

  const onSubmit = async (values: SignUpFormFields) => {
    if (signUpLink?.href) {
      try {
        const response = await axios.post<SignUpResponse>(
          signUpLink.href,
          formatApiRequestData(values, inviteToken, referral.current)
        );
        afterSubmit(
          response.data.response.username,
          response.data.response.clientId
        );

        if (values.emailOptIn) {
          dispatch(signUpMarketingOptInAction());
        }
        dispatch(
          signUpAccountCreatedAction({
            role: values.personaOther || values.persona,
          })
        );
      } catch (e: any) {
        const statusCode = e?.response?.data?.error?.statusCode;
        const errorCode = e?.response?.data?.error?.errorCode;

        if (statusCode === 409) {
          setError('email', { type: 'manual', message: 'Email already exist' });
          navigate({ pathname: routes.signUpBasic.path, search });
        } else if (errorCode === ApiErrorCodeTypes.PASSWORD_TOO_WEAK) {
          setError('password', {
            message: 'Password too weak',
          });
          navigate({ pathname: routes.signUpBasic.path, search });
        } else if (errorCode === ApiErrorCodeTypes.INVALID_EMAIL) {
          setError('email', {
            message: ApiErrorCodeMessages.INVALID_EMAIL,
          });
          navigate({ pathname: routes.signUpBasic.path, search });
        } else {
          dispatch(addAlertAction({ message: apiErrorHandler(e.response) }));
        }
      }
    }
  };

  const isBasicFormValid = getIsBasicFormValid(errors, values);
  const isDetailsFormValid = getIsDetailsFormValid(errors, values);

  const disabledNextStepMap = {
    [routes.signUpBasic.path]: !isBasicFormValid,
    [routes.signUpDetails.path]: !isDetailsFormValid,
  };

  useReferralParam({ referralValidationSchema, referral });
  useSignUpFormSteps({ pathname, isBasicFormValid, setFormStep });
  useCountryQuery({
    countryOptions,
    pathname,
    setValue,
    values,
  });

  return (
    <FormProvider {...form}>
      <chakra.form
        width="100%"
        data-testid="signup-form"
        onSubmit={handleSubmit(onSubmit)}
      >
        {formStep === SignUpFormStep.BASIC && (
          <SignUpBasicForm
            passwordPolicy={passwordPolicy}
            isUBC={isUBC}
            emailParamExists={emailParamExists}
          />
        )}
        {formStep === SignUpFormStep.DETAILS && (
          <SignUpDetailsForm countries={countryOptions} />
        )}
        {formStep === SignUpFormStep.USER_PERSONA && (
          <SignUpPersonaForm userPersonas={userPersonas} />
        )}
        <SignUpActions
          isSubmitting={isSubmitting}
          submitEnabled={isDirty && isValid && Boolean(signUpLink)}
          step={formStep}
          onNext={(pathname) => navigate({ pathname, search })}
          disableNextStep={disabledNextStepMap[pathname]}
          search={search}
        />
      </chakra.form>
    </FormProvider>
  );
};
